import { useCallback, useEffect, useRef, useState, type FormEvent, type JSX } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  api,
  documentToParagraphs,
  formatBytes,
  type Assignment,
  type LessonBlock,
  type LessonDetail,
  type QuizStatus,
  type Submission,
  type UploadedAsset,
} from '../api.ts';
import { AiHelper } from '../components/AiHelper.tsx';
import { trackWatching } from '../lib/youtube.ts';
import { Markup } from '../components/Markup.tsx';
import { FileUpload } from '../components/FileUpload.tsx';

const CONDITION_LABELS: Record<string, string> = {
  view: 'Открыть урок',
  video: 'Досмотреть видео',
  // The wording follows the rule: the tick arrives when the teacher accepts the work, not when
  // it is uploaded, so 'сдать' would promise a tick that does not come.
  assignment: 'Домашнее задание отправлено преподавателю',
  quiz: 'Пройти тест',
  material: 'Посмотреть презентацию',
};

function formatDeadline(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Video from the closed channel, played inside our own page — and counted while it plays.
 *
 * The counting is the part that was missing. The embed already carried enablejsapi=1 and the
 * server already had somewhere to put watch time; nobody was talking to the player, so a video
 * watched to its last second left no trace and "Досмотреть видео" never ticked.
 */
function VideoBlock({
  block,
  onWatched,
}: {
  block: LessonBlock;
  onWatched: () => void;
}): JSX.Element {
  const title = typeof block.config.title === 'string' ? block.config.title : 'Видеоурок';
  const frame = useRef<HTMLIFrameElement>(null);

  // What the server last knew, kept locally so the figure moves as the video plays rather than
  // only when the page is reloaded.
  const [watched, setWatched] = useState(block.watched_fraction ?? 0);

  useEffect(() => {
    const element = frame.current;
    if (!element) return undefined;

    return trackWatching(element, (progress) => {
      void api
        .videoProgress(block.id, {
          watched_seconds: progress.watchedSeconds,
          furthest_seconds: progress.furthestSeconds,
          duration_seconds: progress.durationSeconds,
        })
        .then((update) => {
          if (progress.durationSeconds > 0) {
            setWatched(Math.min(1, progress.watchedSeconds / progress.durationSeconds));
          }

          // The server decides what counts as watched; when it says the lesson moved, the
          // checklist beside the video has to move with it.
          if (update.lesson_completed || progress.ended) onWatched();
        })
        .catch(() => undefined);
    });
  }, [block.id, onWatched]);

  return (
    <div className="block">
      <iframe
        ref={frame}
        className="player"
        src={block.embed_url}
        title={title}
        allow="accelerometer; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
      />
      {watched > 0 ? (
        <p className="muted" style={{ fontSize: '0.84rem', marginTop: 8 }}>
          Просмотрено {Math.round(watched * 100)}%
        </p>
      ) : null}
    </div>
  );
}

/** The files that came back with a past submission, as links the student can reopen. */
function SubmittedFiles({ submission }: { submission: Submission }): JSX.Element | null {
  if (submission.files.length === 0) return null;

  return (
    <ul className="filelist filelist--flat">
      {submission.files.map((file) => (
        <li key={file.id}>
          <a href={api.assetUrl(file.asset_id)} className="filelist__name" download>
            📎 {file.name}
          </a>
          <span className="muted">{formatBytes(file.size_bytes)}</span>
        </li>
      ))}
    </ul>
  );
}

/** Homework: what was handed in, what came back, and the box to hand it in again. */
function AssignmentBlock({
  block,
  onSubmitted,
}: {
  block: LessonBlock;
  onSubmitted: () => void;
}): JSX.Element {
  const assignmentId = block.ref_id;
  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [text, setText] = useState('');
  const [files, setFiles] = useState<UploadedAsset[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!assignmentId) return;

    void api
      .assignment(assignmentId)
      .then(setAssignment)
      .catch(() => setAssignment(null));

    void api
      .submissions(assignmentId)
      .then(setSubmissions)
      .catch(() => setSubmissions([]));
  }, [assignmentId]);

  if (!assignmentId) return <></>;

  const last = submissions[0];
  const waiting = last?.status === 'submitted';
  const graded = last?.status === 'graded';
  const returned = last?.status === 'returned';

  const allowText = assignment?.allow_text ?? true;
  const allowFiles = assignment?.allow_files ?? false;
  const overdue = assignment?.due_at != null && Date.parse(assignment.due_at) < Date.now();

  // The server applies the same rule; this is only so the button explains itself before the click.
  const empty = text.trim().length === 0 && files.length === 0;

  async function send(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!assignmentId) return;
    setBusy(true);
    setError('');

    try {
      const created = await api.submit(assignmentId, {
        ...(text.trim() ? { text: text.trim() } : {}),
        ...(files.length > 0 ? { asset_ids: files.map((f) => f.id) } : {}),
      });
      setSubmissions([created, ...submissions]);
      setText('');
      setFiles([]);
      // Handing work in can be what completes the lesson, so the checklist has to be asked again;
      // otherwise the student sees "not done" next to the work they just sent.
      onSubmitted();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось отправить работу');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="block card">
      <h3 style={{ marginBottom: 6 }}>{assignment?.title ?? 'Домашнее задание'}</h3>

      {assignment?.description_md ? <Markup text={assignment.description_md} /> : null}

      {assignment?.due_at ? (
        <p className={overdue ? 'error' : 'muted'} style={{ fontSize: '0.86rem' }}>
          {overdue ? 'Срок вышел: ' : 'Срок сдачи: '}
          {formatDeadline(assignment.due_at)}
          {overdue && assignment.late_policy === 'allow_marked'
            ? ' — сдать ещё можно, работа будет отмечена как поздняя'
            : ''}
        </p>
      ) : null}

      {graded && last ? (
        <div className="notice notice--done stack">
          <div>
            <strong>Проверено.</strong> Оценка: {Math.round((last.final_fraction ?? 0) * 100)}%
          </div>
          {last.feedback ? <div>{last.feedback}</div> : null}
          {last.text ? <div className="quoted">{last.text}</div> : null}
          <SubmittedFiles submission={last} />
        </div>
      ) : null}

      {waiting && last ? (
        <div className="notice stack">
          <div>Работа отправлена и ждёт проверки.</div>
          {last.text ? <div className="quoted">{last.text}</div> : null}
          <SubmittedFiles submission={last} />
        </div>
      ) : null}

      {returned && last ? (
        <div className="notice notice--accent stack">
          <div>
            <strong>Работа возвращена на доработку.</strong>
          </div>
          {last.feedback ? <div>{last.feedback}</div> : null}

          {/*
           * What was handed in, shown next to why it came back.
           *
           * Only the teacher's remark used to appear here, so somebody asked to correct their work
           * could not see the work: the text they had written was gone from the screen and the
           * file they had attached had nowhere to be clicked. Correcting something you cannot
           * reread means writing it again from nothing.
           */}
          {last.text || last.files.length > 0 ? (
            <div className="stack">
              <div className="muted" style={{ fontSize: '0.84rem' }}>
                Вы отправляли:
              </div>
              {last.text ? <div className="quoted">{last.text}</div> : null}
              <SubmittedFiles submission={last} />
            </div>
          ) : (
            <div className="muted" style={{ fontSize: '0.84rem' }}>
              К прошлой отправке не было ни текста, ни файла.
            </div>
          )}
        </div>
      ) : null}

      {/*
       * Every earlier attempt, with its files still reachable.
       *
       * A second attempt used to push the first one out of sight, taking the files with it — so
       * the thing a learner most often wants at exactly this moment, their own last version to
       * download and build on, was the one thing the page had stopped showing.
       */}
      {submissions.length > 1 ? (
        <details className="attempts">
          <summary>Прошлые отправки ({submissions.length - 1})</summary>
          {submissions.slice(1).map((past) => (
            <div key={past.id} className="attempts__item">
              <div className="muted" style={{ fontSize: '0.82rem' }}>
                Попытка {past.attempt_number}
                {past.status === 'returned' ? ' · возвращена' : ''}
                {past.status === 'graded' ? ' · проверена' : ''}
              </div>
              {past.text ? <div className="quoted">{past.text}</div> : null}
              <SubmittedFiles submission={past} />
              {past.feedback ? (
                <div className="muted" style={{ fontSize: '0.84rem' }}>
                  Замечание: {past.feedback}
                </div>
              ) : null}
            </div>
          ))}
        </details>
      ) : null}

      {/* Once graded there is nothing to do; a form here would only invite a pointless retry. */}
      {!graded && !waiting ? (
        <form onSubmit={(e) => void send(e)} style={{ marginTop: 16 }}>
          {error ? <p className="error">{error}</p> : null}

          {allowText ? (
            <div className="field">
              <label htmlFor={`answer-${block.id}`}>Ваш ответ</label>
              <textarea
                id={`answer-${block.id}`}
                className="input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Решение, ссылка на репозиторий или пояснение"
              />
            </div>
          ) : null}

          {allowFiles && assignment ? (
            <div className="field">
              <label>Файлы к работе</label>
              <FileUpload
                maxFiles={assignment.max_files}
                maxFileMb={assignment.max_file_mb}
                files={files}
                onChange={setFiles}
                disabled={busy}
              />
            </div>
          ) : null}

          <button className="btn" type="submit" disabled={busy || empty}>
            {busy ? 'Отправляем…' : returned ? 'Отправить исправленное' : 'Отправить работу'}
          </button>
          {empty ? (
            <span className="muted" style={{ marginLeft: 12, fontSize: '0.84rem' }}>
              {allowFiles && !allowText
                ? 'Приложите файл'
                : allowFiles
                  ? 'Напишите ответ или приложите файл'
                  : 'Напишите ответ'}
            </span>
          ) : null}
        </form>
      ) : null}

      {submissions.length > 1 ? (
        <p className="muted" style={{ fontSize: '0.84rem', marginTop: 14 }}>
          Попыток отправлено: {submissions.length}
          {assignment && assignment.max_attempts > 0 ? ` из ${assignment.max_attempts}` : ''}
        </p>
      ) : null}
    </section>
  );
}

/** Quiz: status and the door into the attempt. */
function QuizBlock({ block }: { block: LessonBlock }): JSX.Element {
  const quizId = block.ref_id;
  const navigate = useNavigate();
  const [status, setStatus] = useState<QuizStatus | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!quizId) return;
    api
      .quizStatus(quizId)
      .then(setStatus)
      .catch(() => setStatus(null));
  }, [quizId]);

  if (!quizId || !status) return <></>;

  const outOfAttempts = status.attempts_left === 0 && !status.passed;
  const resuming = status.open_attempt_id !== null;

  /**
   * One button, three meanings.
   *
   * Starting an attempt when one is already open returns that same attempt rather than a new one,
   * so this is safe to press twice — but the label still says "continue", because a student who
   * reads "start" after a dropped connection reasonably fears losing what they already answered.
   */
  function open(): void {
    if (!quizId) return;

    if (status?.open_attempt_id) {
      void navigate(`/attempts/${status.open_attempt_id}`);
      return;
    }

    setBusy(true);
    setError('');
    api
      .startAttempt(quizId)
      .then((attempt) => navigate(`/attempts/${attempt.id}`))
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'Не удалось начать тест'),
      )
      .finally(() => setBusy(false));
  }

  return (
    <section className="block card">
      <h3 style={{ marginBottom: 10 }}>{status.title}</h3>

      <p className="muted" style={{ marginTop: 0 }}>
        Вопросов: {status.question_count}. Проходной балл — {Math.round(status.pass_fraction * 100)}
        %.{' '}
        {status.attempts_left === null
          ? 'Попытки не ограничены.'
          : `Осталось попыток: ${status.attempts_left}.`}
        {status.time_limit_min ? ` На выполнение — ${status.time_limit_min} мин.` : ''}
      </p>

      {error ? <p className="error">{error}</p> : null}

      {status.passed ? (
        <div className="notice notice--done">
          Тест пройден. Лучший результат — {Math.round((status.best_fraction ?? 0) * 100)}%.
        </div>
      ) : status.best_fraction !== null ? (
        <div className="notice notice--accent">
          Лучший результат — {Math.round(status.best_fraction * 100)}%. Нужно{' '}
          {Math.round(status.pass_fraction * 100)}%.
        </div>
      ) : null}

      {resuming ? <div className="notice">У вас есть незаконченная попытка.</div> : null}

      <div className="row" style={{ marginTop: 16 }}>
        <button
          className="btn"
          type="button"
          disabled={busy || (outOfAttempts && !resuming) || status.question_count === 0}
          onClick={open}
        >
          {busy
            ? 'Открываем…'
            : resuming
              ? 'Продолжить попытку'
              : status.attempts_used === 0
                ? 'Начать тест'
                : status.passed
                  ? 'Пройти ещё раз'
                  : 'Пересдать'}
        </button>

        {status.can_review && status.last_attempt_id ? (
          <Link to={`/attempts/${status.last_attempt_id}`} className="btn btn--ghost">
            Разбор ответов
          </Link>
        ) : null}

        {outOfAttempts && !resuming ? <span className="muted">Попытки закончились</span> : null}
        {status.question_count === 0 ? (
          <span className="muted">В тесте пока нет вопросов</span>
        ) : null}
      </div>
    </section>
  );
}

/**
 * A file attached to the lesson.
 *
 * A presentation is something to read, not something to collect: made to download, it lands in a
 * folder and the lesson stops there. So anything the browser can render — a PDF, an image — opens
 * in the page, and only what it genuinely cannot show is offered as a download.
 *
 * .pptx is the one that cannot: no browser renders it, so it stays a download. The lesson editor
 * says so at the point where a teacher would otherwise upload one.
 */
function FileBlock({ block, onSeen }: { block: LessonBlock; onSeen: () => void }): JSX.Element {
  const assetId = typeof block.config.asset_id === 'string' ? block.config.asset_id : null;
  const name = typeof block.config.title === 'string' ? block.config.title : 'Материал к уроку';
  const mime = block.asset_mime ?? '';

  if (!assetId) return <></>;

  const isImage = mime.startsWith('image/');
  // The server decides: a .pptx is unshowable in itself and showable through its rendering.
  const canShow = block.asset_viewable === true;
  const frame = useRef<HTMLDivElement>(null);
  const reported = useRef(false);

  /*
   * Counted as read once it has actually been on screen.
   *
   * Not on mount: a presentation five screens down would be marked read by a pupil who never
   * scrolled to it. Not on a timer either — measuring how long a child looked at a slide teaches
   * them to leave the tab open. Having it in front of them is the honest test.
   */
  useEffect(() => {
    const element = frame.current;
    if (!element || !assetId) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting) || reported.current) return;

        reported.current = true;
        observer.disconnect();

        void api
          .materialSeen(block.id)
          .then(onSeen)
          .catch(() => {
            // A failed report is not the pupil's problem; the next visit tries again.
            reported.current = false;
          });
      },
      { threshold: 0.4 },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [assetId, block.id, onSeen]);

  if (canShow && !isImage) {
    return (
      <figure className="block viewer" ref={frame}>
        <figcaption className="viewer__head">
          <span className="viewer__name">{name}</span>
          {/*
           * Reading, and only reading. There is no download button: a presentation offered as a
           * file gets filed away instead of read, which is the habit this whole block exists to
           * break. Full screen stays, because a cramped frame on a laptop is the one thing that
           * would send somebody looking for the download again.
           */}
          <a
            className="btn btn--ghost btn--small"
            href={api.assetViewUrl(assetId)}
            target="_blank"
            rel="noreferrer"
          >
            Во весь экран
          </a>
        </figcaption>

        {/*
         * An iframe rather than <embed>: it is the one element every browser routes through its
         * own PDF viewer, and the one that degrades to a link instead of a broken plugin box.
         */}
        {/*
         * #toolbar=0 asks Chrome and Edge to drop their own download and print buttons from the
         * embedded viewer. A request, not a lock: the file is still reachable to anyone who wants
         * it, and nothing served to a browser can be otherwise.
         */}
        <iframe
          className="viewer__frame"
          src={`${api.assetViewUrl(assetId)}#toolbar=0`}
          title={name}
        />
      </figure>
    );
  }

  if (canShow && isImage) {
    return (
      <figure className="block viewer viewer--image" ref={frame}>
        <img src={api.assetViewUrl(assetId)} alt={name} loading="lazy" />
        <figcaption className="viewer__caption">{name}</figcaption>
      </figure>
    );
  }

  return (
    <div className="block" ref={frame}>
      <a className="btn btn--ghost" href={api.assetUrl(assetId)} download>
        📎 {name}
      </a>
    </div>
  );
}

function TextBlock({ block }: { block: LessonBlock }): JSX.Element {
  const text = typeof block.config.text === 'string' ? block.config.text : '';
  if (!text) return <></>;

  return (
    <div className="block prose">
      <Markup text={text} />
    </div>
  );
}

/**
 * The end of the lesson: where the course goes next, and whether it is open yet.
 *
 * At the bottom of the page rather than in the sidebar, because that is where somebody is when
 * they have finished reading — the checklist beside the lesson answers "what is left", and this
 * answers "what now", which is a different question asked at a different moment.
 *
 * The button is dead until the lesson is complete, and says why. A disabled control with no
 * explanation is the interface refusing without giving a reason; the checklist above already
 * lists the missing steps, so this only has to point back at it.
 */
function LessonFooter({ lesson }: { lesson: LessonDetail }): JSX.Element {
  const done = lesson.state === 'completed';
  // Handed in and waiting for the teacher: the way on is open, the tick is not yet given.
  const waiting = lesson.state === 'submitted';
  const passed = done || waiting;
  const left = lesson.completion.filter((item) => !item.met).length;

  if (!lesson.next) {
    return (
      <div className="lessonend">
        <p className="muted lessonend__note">Это последний урок курса.</p>
        <Link to={`/courses/${lesson.course_id}`} className="btn btn--ghost">
          К курсу
        </Link>
      </div>
    );
  }

  return (
    <div className="lessonend">
      <p className="muted lessonend__note">
        {done ? (
          <>
            Дальше: <strong>{lesson.next.title}</strong>
          </>
        ) : waiting ? (
          <>
            Работа сдана, следующий урок открыт: <strong>{lesson.next.title}</strong>. В рейтинг
            этот урок пойдёт, когда преподаватель примет работу.
          </>
        ) : (
          // Counted rather than listed: the list is directly above, and repeating it here would
          // make the page say the same thing twice in two places that can drift apart.
          `Чтобы открыть следующий урок, закончите начатое — осталось пунктов: ${left}`
        )}
      </p>

      {passed ? (
        <Link to={`/lessons/${lesson.next.id}`} className="btn">
          Следующий урок →
        </Link>
      ) : (
        <button type="button" className="btn" disabled>
          Следующий урок →
        </button>
      )}
    </div>
  );
}

/**
 * One lesson: prose, then the blocks in the order the teacher arranged them.
 *
 * Opening the page reports the visit, which is what most lessons count as completion. The
 * checklist on the right always shows what is still missing — a learner should never have to
 * guess why a lesson is not ticked off.
 */
export function LessonPage(): JSX.Element {
  const { lessonId } = useParams();
  const [lesson, setLesson] = useState<LessonDetail | null>(null);
  const [error, setError] = useState('');
  const reported = useRef<string | null>(null);

  /** Re-reads the lesson so the completion checklist reflects what just happened. */
  const refresh = useCallback((): void => {
    if (!lessonId) return;
    void api
      .lesson(lessonId)
      .then(setLesson)
      .catch(() => undefined);
  }, [lessonId]);

  useEffect(() => {
    if (!lessonId) return;

    api
      .lesson(lessonId)
      .then(async (loaded) => {
        setLesson(loaded);

        // Report the visit once per lesson, then refresh so the checklist reflects it.
        if (reported.current !== lessonId) {
          reported.current = lessonId;
          await api.markViewed(lessonId).catch(() => undefined);
          setLesson(await api.lesson(lessonId));
        }
      })
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'Не удалось открыть урок'),
      );
  }, [lessonId]);

  if (error) {
    return (
      <div style={{ paddingTop: 40 }} className="stack">
        <div className="notice notice--accent">{error}</div>
        <Link to="/" className="back">
          ← Мои курсы
        </Link>
      </div>
    );
  }

  if (!lesson) {
    return (
      <p className="muted" style={{ paddingTop: 40 }}>
        Загружаем…
      </p>
    );
  }

  const paragraphs = documentToParagraphs(lesson.content);

  return (
    <>
      <div className="page-head">
        <Link to={`/courses/${lesson.course_id}`} className="back">
          ← К курсу
        </Link>
        <h1>{lesson.title}</h1>
      </div>

      <div className="lesson-layout">
        <article>
          {paragraphs.length > 0 ? (
            <div className="prose">
              {paragraphs.map((line, index) => (
                <p key={index}>{line}</p>
              ))}
            </div>
          ) : null}

          {lesson.blocks.map((block) => {
            if (block.type === 'text') return <TextBlock key={block.id} block={block} />;
            if (block.type === 'video') {
              return <VideoBlock key={block.id} block={block} onWatched={refresh} />;
            }
            if (block.type === 'assignment') {
              return <AssignmentBlock key={block.id} block={block} onSubmitted={refresh} />;
            }
            if (block.type === 'quiz') return <QuizBlock key={block.id} block={block} />;
            if (block.type === 'file' || block.type === 'image') {
              return <FileBlock key={block.id} block={block} onSeen={refresh} />;
            }
            return null;
          })}

          <LessonFooter lesson={lesson} />
        </article>

        <aside className="checklist">
          <h3>Чтобы засчитать урок</h3>

          {/*
           * The share, and what it is a share of.
           *
           * "60%" on its own was read as progress through the course, while the teacher's roll
           * showed 30% for the same child — both correct, one about this lesson and one about the
           * whole course, and neither saying which. A number nobody can attribute is worse than
           * no number: it makes two honest screens look like they disagree.
           */}
          {/*
           * Once everything is sent the count would read 100% on a lesson the teacher has not
           * accepted — the same contradiction as a green tick on returned work. Said plainly instead.
           */}
          {lesson.state === 'submitted' ? (
            <p className="checklist__share">Всё отправлено — урок на проверке</p>
          ) : lesson.completion.length > 0 ? (
            <p className="checklist__share">
              Этот урок пройден на{' '}
              {Math.round(
                (lesson.completion.filter((i) => i.met).length / lesson.completion.length) * 100,
              )}
              % — {lesson.completion.filter((i) => i.met).length} из {lesson.completion.length}
            </p>
          ) : null}

          <ul>
            {lesson.completion.map((item, index) => (
              <li key={index} data-met={item.met}>
                <span>{item.met ? '✓' : '○'}</span>
                <span>{CONDITION_LABELS[item.type] ?? item.type}</span>
              </li>
            ))}
          </ul>

          {lesson.state === 'completed' ? (
            <div className="notice notice--done" style={{ marginTop: 18 }}>
              Урок пройден
            </div>
          ) : null}

          {/*
           * Said out loud, because the checklist alone would mislead: the homework line stays
           * unticked until the teacher accepts it, and without this a pupil who has done
           * everything reads that as "not finished" and does not dare go on.
           */}
          {lesson.state === 'submitted' ? (
            <div className="notice notice--accent" style={{ marginTop: 18 }}>
              Работа сдана и ждёт проверки. Следующий урок уже открыт — можно двигаться дальше. В
              рейтинг урок засчитается после того, как преподаватель примет работу.
            </div>
          ) : null}

          {/* Under the checklist, where somebody looks when a step will not tick. */}
          <AiHelper lessonId={lesson.id} />
        </aside>
      </div>
    </>
  );
}
